/** Shared col-span utilities for the Customize Layout mini-grid editor. */

import { moveItemInArray } from '@angular/cdk/drag-drop';
import { stripProductPrefix } from './dashboard-embed.helpers';

/** Above navbar/sidebar (z-[999]) and inline editors (z-[10000]). */
export const LAYOUT_EDITOR_OVERLAY_Z_CLASS = 'z-[10010]';

const LAYOUT_EDITOR_SCROLL_LOCK_CLASS = 'layout-editor-scroll-lock';
let layoutEditorScrollLockCount = 0;

/** Prevent background scroll while a customize-layout overlay is open. */
export function lockLayoutEditorScroll(): void {
  if (typeof document === 'undefined') return;
  layoutEditorScrollLockCount++;
  if (layoutEditorScrollLockCount === 1) {
    document.documentElement.classList.add(LAYOUT_EDITOR_SCROLL_LOCK_CLASS);
  }
}

export function unlockLayoutEditorScroll(): void {
  if (typeof document === 'undefined') return;
  layoutEditorScrollLockCount = Math.max(0, layoutEditorScrollLockCount - 1);
  if (layoutEditorScrollLockCount === 0) {
    document.documentElement.classList.remove(LAYOUT_EDITOR_SCROLL_LOCK_CLASS);
  }
}

export const LAYOUT_EDITOR_COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

const ALLOWED_COL_SPANS = [3, 4, 6, 8, 12];

export function normalizeLayoutEditorColSpan(span: number | null | undefined): number {
  if (span == null || !Number.isFinite(span)) return 12;
  const n = Math.round(span);
  return ALLOWED_COL_SPANS.includes(n) ? n : 12;
}

export function getLayoutEditorColSpanClass(span: number): string {
  switch (normalizeLayoutEditorColSpan(span)) {
    case 3:
      return 'col-span-3';
    case 4:
      return 'col-span-4';
    case 6:
      return 'col-span-6';
    case 8:
      return 'col-span-8';
    case 12:
    default:
      return 'col-span-12';
  }
}

export function getLayoutEditorColSpanLabel(span: number): string {
  switch (normalizeLayoutEditorColSpan(span)) {
    case 4:
      return 'Small';
    case 6:
      return 'Medium';
    case 8:
      return 'Large';
    case 12:
      return 'Full';
    default:
      return `${span}`;
  }
}

/**
 * Whether the "decrease width" stepper is active for a card.
 *
 * When `minColSpan` is provided, a card cannot shrink below its designed
 * default — e.g. a card designed for `col-8` can only step between 8 and
 * 12, never down to 6 or 4. When omitted, the stepper is unrestricted
 * (matches the original behaviour).
 */
export function canDecreaseLayoutEditorColSpan(
  colSpan: number,
  minColSpan?: number | null,
): boolean {
  const idx = LAYOUT_EDITOR_COL_SPAN_STEPS.indexOf(
    normalizeLayoutEditorColSpan(colSpan),
  );
  if (idx <= 0) return false;
  if (minColSpan != null && Number.isFinite(minColSpan)) {
    const min = normalizeLayoutEditorColSpan(minColSpan);
    if (LAYOUT_EDITOR_COL_SPAN_STEPS[idx - 1] < min) return false;
  }
  return true;
}

export function canIncreaseLayoutEditorColSpan(colSpan: number): boolean {
  const idx = LAYOUT_EDITOR_COL_SPAN_STEPS.indexOf(normalizeLayoutEditorColSpan(colSpan));
  return idx >= 0 && idx < LAYOUT_EDITOR_COL_SPAN_STEPS.length - 1;
}

/** Tailwind classes for the live dashboard 12-col grid (matches editor proportions). */
export function getLiveSectionColClass(colSpan: number): string {
  switch (normalizeLayoutEditorColSpan(colSpan)) {
    case 4:
      return 'col-span-12 lg:col-span-4';
    case 6:
      return 'col-span-12 lg:col-span-6';
    case 8:
      return 'col-span-12 lg:col-span-8';
    case 12:
    default:
      return 'col-span-12';
  }
}

/**
 * 12-col grid container on My Dashboard (`my-dashboard.component.html`).
 * Keep the customize-state popup preview on the same classes so tile packing
 * matches the live unified grid (including `grid-flow-row-dense`).
 */
export const UNIFIED_DASHBOARD_GRID_CLASS =
  'grid grid-cols-12 gap-4 grid-flow-row-dense';

/**
 * Tailwind col-span classes for a unified My Dashboard tile — must stay in
 * sync with {@link MyDashboardComponent.getWidgetColClass}.
 */
export function getUnifiedDashboardTileColClass(colSpan: number): string {
  switch (normalizeLayoutEditorColSpan(colSpan)) {
    case 3:
      return 'col-span-12 sm:col-span-6 lg:col-span-3';
    case 4:
      return 'col-span-12 sm:col-span-6 lg:col-span-4';
    case 6:
      return 'col-span-12 lg:col-span-6';
    case 8:
      return 'col-span-12 lg:col-span-8';
    case 12:
      return 'col-span-12';
    default:
      return 'col-span-12';
  }
}

/**
 * Looks up a designed on-screen col-span for a catalog `widget_key`, including
 * product-prefixed keys (`cr_ic_*`, `de_vb_*`, …) via strip + suffix rules.
 */
export function resolveOnScreenColSpanByWidgetKey(
  widgetKey: string,
  map: Readonly<Record<string, number>>,
): number | undefined {
  if (!widgetKey) return undefined;

  const direct = map[widgetKey];
  if (direct != null) return direct;

  const stripped = stripProductPrefix(widgetKey);
  const fromStripped = map[stripped];
  if (fromStripped != null) return fromStripped;

  for (const [canonicalKey, span] of Object.entries(map)) {
    const suffix = '_' + canonicalKey;
    if (
      widgetKey.endsWith(suffix) ||
      stripped.endsWith(suffix) ||
      widgetKey === canonicalKey ||
      stripped === canonicalKey
    ) {
      return span;
    }
  }

  return undefined;
}

/**
 * Resolves the col-span a unified My Dashboard tile should render at.
 * Designed widths from the map win unless the user/API persisted a different
 * step (e.g. resized via Edit Your Settings).
 */
export function resolveUnifiedWidgetColSpan(
  widgetKey: string,
  map: Readonly<Record<string, number>>,
  persistedColSpan?: number | null,
): number {
  const fromMap = resolveOnScreenColSpanByWidgetKey(widgetKey, map);
  const normalizedPersisted =
    persistedColSpan != null && Number.isFinite(persistedColSpan)
      ? normalizeLayoutEditorColSpan(persistedColSpan)
      : null;

  if (fromMap != null) {
    const designed = normalizeLayoutEditorColSpan(fromMap); 
    if (normalizedPersisted != null) {
      return normalizedPersisted;
    }
    return designed;
  }

  return normalizedPersisted ?? 12;
}

export interface LayoutEditorDraftItem {
  id: string;
  visible: boolean;
  colSpan: number;
  order: number;
}

/** Minimum fields required for visibility-toggle reordering. */
export interface LayoutEditorVisibilityItem {
  id: string;
  visible: boolean;
  order: number;
}

/** Minimal drag-drop indices (decoupled from Angular CDK in consumers/tests). */
export interface LayoutEditorDragDropEvent {
  previousIndex: number;
  currentIndex: number;
}

export type LayoutEditorDropStrategy =
  /** CDK-style shift: cards between source and target slide (legacy editor pages). */
  | 'shift'
  /** Swap only the two array entries; each card keeps its own colSpan/rowSpan (MR2). */
  | 'pairwise-swap'
  /**
   * Swap card identity between two slots while preserving each slot's grid
   * footprint (colSpan/rowSpan) when both cards are resizable (MR1).
   */
  | 'slot-preserving-swap'
  /**
   * Swap card identity; each slot keeps its original colSpan (my-dashboard
   * customize preview).
   */
  | 'slot-pin-colSpan';

export interface LayoutEditorDropBounds {
  minIndex: number;
  maxIndex: number;
}

/** Minimum shape for drag-drop reorder helpers. */
export interface LayoutEditorDropItem {
  colSpan: number;
  rowSpan?: number;
  visible?: boolean;
  order?: number;
}

export interface LayoutEditorCardDropOptions<T extends LayoutEditorDropItem> {
  strategy: LayoutEditorDropStrategy;
  /** When true (default), visible cards cannot be dropped below hidden ones. */
  clampToLastVisible?: boolean;
  /**
   * Restrict the drop target index (e.g. daily-cron group boundaries).
   * Last-visible clamping, when enabled, is scoped to this range.
   */
  getDropBounds?: (items: readonly T[], fromIndex: number) => LayoutEditorDropBounds | null;
  /** slot-preserving-swap: both resizable → swap footprints; else keep own span. */
  isResizable?: (item: T) => boolean;
  defaultRowSpan?: (item: T) => number;
  /** Renumber `order` to match array index after drop. Default true. */
  renumberOrder?: boolean;
}

export interface LayoutEditorGroupDropItem extends LayoutEditorDropItem {
  group: string;
}

function lastVisibleIndexInRange<T extends { visible?: boolean }>(
  items: readonly T[],
  minIndex: number,
  maxIndex: number,
): number {
  let last = -1;
  for (let i = minIndex; i <= maxIndex; i++) {
    if (items[i]?.visible) last = i;
  }
  return last;
}

/**
 * Resolves min/max drop indices for items sharing the dragged row's `group`.
 * Used by daily-cron / overdue-cron so cards cannot cross overview/analytics
 * boundaries in the customize popup.
 */
export function resolveLayoutEditorGroupDropBounds<T extends LayoutEditorGroupDropItem>(
  items: readonly T[],
  fromIndex: number,
): LayoutEditorDropBounds | null {
  const dragged = items[fromIndex];
  if (!dragged) return null;

  const groupStart = items.findIndex((i) => i.group === dragged.group);
  const groupEndReverse = items
    .slice()
    .reverse()
    .findIndex((i) => i.group === dragged.group);
  if (groupStart === -1 || groupEndReverse === -1) return null;

  return {
    minIndex: groupStart,
    maxIndex: items.length - 1 - groupEndReverse,
  };
}

function resolveDropTargetIndex<T extends LayoutEditorDropItem>(
  items: readonly T[],
  fromIndex: number,
  rawTarget: number,
  options: LayoutEditorCardDropOptions<T>,
): number | null {
  if (fromIndex === rawTarget) return null;

  let target = rawTarget;
  const bounds = options.getDropBounds?.(items, fromIndex) ?? null;

  if (bounds) {
    target = Math.max(bounds.minIndex, Math.min(target, bounds.maxIndex));
  }

  if (options.clampToLastVisible !== false) {
    const dragged = items[fromIndex];
    if (dragged?.visible) {
      const rangeMin = bounds?.minIndex ?? 0;
      const rangeMax = bounds?.maxIndex ?? items.length - 1;
      const lastVisibleIdx = lastVisibleIndexInRange(items, rangeMin, rangeMax);
      if (lastVisibleIdx >= 0) {
        target = Math.min(target, lastVisibleIdx);
      }
    }
  }

  if (fromIndex === target) return null;
  if (
    fromIndex < 0 ||
    target < 0 ||
    fromIndex >= items.length ||
    target >= items.length
  ) {
    return null;
  }

  return target;
}

function applySlotPreservingSwap<T extends LayoutEditorDropItem>(
  items: T[],
  from: number,
  to: number,
  options: LayoutEditorCardDropOptions<T>,
): void {
  const fromItem = items[from];
  const toItem = items[to];
  const fromResizable = options.isResizable?.(fromItem) ?? false;
  const toResizable = options.isResizable?.(toItem) ?? false;
  const bothResizable = fromResizable && toResizable;
  const defaultRowSpan = options.defaultRowSpan ?? (() => 1);

  const swappedFromColSpan = bothResizable ? toItem.colSpan : fromItem.colSpan;
  const swappedToColSpan = bothResizable ? fromItem.colSpan : toItem.colSpan;
  const swappedFromRowSpan = bothResizable
    ? (toItem.rowSpan ?? defaultRowSpan(toItem))
    : (fromItem.rowSpan ?? defaultRowSpan(fromItem));
  const swappedToRowSpan = bothResizable
    ? (fromItem.rowSpan ?? defaultRowSpan(fromItem))
    : (toItem.rowSpan ?? defaultRowSpan(toItem));

  items[from] = { ...toItem, colSpan: swappedFromColSpan, rowSpan: swappedFromRowSpan };
  items[to] = { ...fromItem, colSpan: swappedToColSpan, rowSpan: swappedToRowSpan };
}

function applySlotPinColSpanSwap<T extends LayoutEditorDropItem>(
  items: T[],
  from: number,
  to: number,
): void {
  const fromItem = items[from];
  const toItem = items[to];
  items[from] = { ...toItem, colSpan: fromItem.colSpan };
  items[to] = { ...fromItem, colSpan: toItem.colSpan };
}

/**
 * Applies a customize-layout card drop using the requested strategy.
 *
 * Returns a new array when the drop changes order/content, or `null` when the
 * drop is a no-op (same index, out of bounds, or clamped back to source).
 */
export function applyLayoutEditorCardDrop<T extends LayoutEditorDropItem>(
  items: readonly T[],
  event: LayoutEditorDragDropEvent,
  options: LayoutEditorCardDropOptions<T>,
): T[] | null {
  if (event.previousIndex === event.currentIndex) return null;

  const from = event.previousIndex;
  const to = resolveDropTargetIndex(items, from, event.currentIndex, options);
  if (to === null) return null;

  const next = items.slice();

  switch (options.strategy) {
    case 'shift':
      moveItemInArray(next, from, to);
      break;
    case 'pairwise-swap':
      [next[from], next[to]] = [next[to], next[from]];
      break;
    case 'slot-preserving-swap':
      applySlotPreservingSwap(next, from, to, options);
      break;
    case 'slot-pin-colSpan':
      applySlotPinColSpanSwap(next, from, to);
      break;
  }

  if (options.renumberOrder !== false) {
    next.forEach((row, idx) => {
      if ('order' in row && row.order !== undefined) {
        (row as T & { order: number }).order = idx;
      }
    });
  }

  return next;
}

export function reorderLayoutEditorItemsOnVisibilityToggle<T extends LayoutEditorVisibilityItem>(
  items: T[],
  id: string,
): T[] {
  const idx = items.findIndex((i) => i.id === id);
  if (idx === -1) return items;
  const current = items[idx];
  const updated = { ...current, visible: !current.visible };
  const without = items.filter((_, i) => i !== idx);
  let next: T[];
  if (current.visible) {
    next = [...without, updated];
  } else {
    const firstHiddenIdx = without.findIndex((i) => !i.visible);
    next =
      firstHiddenIdx === -1
        ? [...without, updated]
        : [...without.slice(0, firstHiddenIdx), updated, ...without.slice(firstHiddenIdx)];
  }
  return next.map((row, order) => ({ ...row, order }));
}

/**
 * Minimum shape an item needs for the Auto Adjust Width helper.
 *
 * The helper does not care about `title`, `order`, or any consumer-specific
 * fields — it only needs to know which cards are currently visible and what
 * their col-span is. Consumer item types (e.g. each page's
 * `LayoutEditorItem`) already satisfy this shape, so they can be passed
 * directly.
 */
export interface LayoutEditorAutoWidthItem {
  id: string;
  visible: boolean;
  colSpan: number;
}

/**
 * Computes the `cardId → new colSpan` updates that Auto Adjust Width should
 * apply for the given Customize Layout draft items.
 *
 * Algorithm:
 *  1. Replays the same dense packing the live grid does (12-column wide,
 *     row-span-2 honored for "tall" tiles via the optional `isTall` callback).
 *  2. For every row that still has leftover empty columns:
 *      a. If multiple cards in the row are tied at the smallest current
 *         col-span AND the leftover divides evenly into them AND the
 *         resulting per-card width snaps to an allowed step (4/6/8/12),
 *         every tied card grows by the same amount (e.g. two col-4 cards
 *         with leftover 4 → both become col-6 instead of one becoming
 *         col-8 and the other staying col-4).
 *      b. Otherwise, the single smallest card in the row grows by the
 *         largest snap-allowed step that fits in the leftover.
 *  3. Re-simulates after every bump so cascading effects (e.g. a tall tile
 *     collapsing to a single row once its colSpan no longer matches the
 *     "tall" threshold) are reflected before picking the next bump.
 *
 * Tall (row-span-2) tiles are skipped as candidates unless they are the only
 * card occupying the leaky row — in which case growing them is the only way
 * to fill the gap (and they typically collapse to a single row at colSpan=12,
 * naturally freeing the row(s) below).
 *
 * Returns an empty map when nothing can be grown (every visible row is
 * already full).
 */
export function computeLayoutEditorAutoWidthUpdates<T extends LayoutEditorAutoWidthItem>(
  items: readonly T[],
  isTall?: (item: T) => boolean,
): Map<string, number> {
  const finalUpdates = new Map<string, number>();
  const visible = items.filter((i) => i.visible);
  if (!visible.length) return finalUpdates;

  const GRID_COLS = 12;
  const MAX_ITERATIONS = Math.max(50, visible.length * 4);
  const STEPS = LAYOUT_EDITOR_COL_SPAN_STEPS;

  const effectiveSpan = (item: T): number =>
    finalUpdates.get(item.id) ?? normalizeLayoutEditorColSpan(item.colSpan);
  const effectiveRowSpan = (item: T): number => {
    if (!isTall) return 1;
    const virtual: T = { ...item, colSpan: effectiveSpan(item) };
    return isTall(virtual) ? 2 : 1;
  };

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    const grid: boolean[][] = [];
    const ensureRow = (r: number): void => {
      while (grid.length <= r) grid.push(new Array(GRID_COLS).fill(false));
    };
    const primaryRow = new Map<string, number>();
    const rowSpanOf = new Map<string, number>();

    for (const item of visible) {
      const width = effectiveSpan(item);
      const rowSpan = effectiveRowSpan(item);
      rowSpanOf.set(item.id, rowSpan);

      let placed = false;
      let row = 0;
      while (!placed) {
        ensureRow(row + rowSpan - 1);
        for (let col = 0; col <= GRID_COLS - width && !placed; col++) {
          let fits = true;
          for (let r = 0; r < rowSpan && fits; r++) {
            for (let c = col; c < col + width && fits; c++) {
              if (grid[row + r][c]) fits = false;
            }
          }
          if (fits) {
            for (let r = 0; r < rowSpan; r++) {
              for (let c = col; c < col + width; c++) grid[row + r][c] = true;
            }
            primaryRow.set(item.id, row);
            placed = true;
          }
        }
        if (!placed) row++;
      }
    }

    let bumped = false;
    for (let r = 0; r < grid.length; r++) {
      const occupied = grid[r].filter(Boolean).length;
      const leftover = GRID_COLS - occupied;
      if (leftover <= 0) continue;

      // Prefer non-tall single-row tiles whose primary row is this row.
      let candidates = visible.filter(
        (i) => primaryRow.get(i.id) === r && (rowSpanOf.get(i.id) ?? 1) === 1,
      );

      // Fallback: if no non-tall candidate exists, allow a tall tile whose
      // footprint covers this row to be grown (it will collapse to a single
      // row once its new colSpan no longer matches the "tall" threshold).
      if (!candidates.length) {
        candidates = visible.filter((i) => {
          const rs = rowSpanOf.get(i.id) ?? 1;
          if (rs === 1) return false;
          const pr = primaryRow.get(i.id);
          return pr !== undefined && r >= pr && r < pr + rs;
        });
      }
      if (!candidates.length) continue;

      let smallestSpan = Infinity;
      for (const c of candidates) {
        const cur = effectiveSpan(c);
        if (cur < smallestSpan) smallestSpan = cur;
      }
      const smallestCandidates = candidates.filter(
        (c) => effectiveSpan(c) === smallestSpan,
      );
      if (!smallestCandidates.length) continue;

      const n = smallestCandidates.length;
      let bumpsForRow: { id: string; newSpan: number }[] | null = null;

      if (n > 1 && leftover % n === 0) {
        const sharedTarget = smallestSpan + leftover / n;
        if (sharedTarget > smallestSpan && STEPS.includes(sharedTarget)) {
          bumpsForRow = smallestCandidates.map((c) => ({
            id: c.id,
            newSpan: sharedTarget,
          }));
        }
      }

      if (!bumpsForRow) {
        const smallest = smallestCandidates[0];
        const target = smallestSpan + leftover;
        let best = smallestSpan;
        for (const step of STEPS) {
          if (step > smallestSpan && step <= target && step > best) best = step;
        }
        if (best !== smallestSpan) {
          bumpsForRow = [{ id: smallest.id, newSpan: best }];
        }
      }

      if (bumpsForRow) {
        for (const u of bumpsForRow) finalUpdates.set(u.id, u.newSpan);
        bumped = true;
        // Re-simulate so cascading effects (e.g. a tall tile collapsing to a
        // single row) are reflected before picking the next bump.
        break;
      }
    }

    if (!bumped) break;
  }

  return finalUpdates;
}

/**
 * Convenience: applies the updates returned by
 * {@link computeLayoutEditorAutoWidthUpdates} to a draft array, returning a
 * new array (the original is not mutated). Items with no update keep their
 * existing reference.
 */
export function applyLayoutEditorAutoWidthUpdates<T extends LayoutEditorAutoWidthItem>(
  items: readonly T[],
  updates: Map<string, number>,
): T[] {
  if (!updates.size) return items.slice();
  return items.map((i) =>
    updates.has(i.id) ? { ...i, colSpan: updates.get(i.id) as number } : i,
  );
}

export function buildLayoutMapFromEditorDrafts<
  T extends string,
  L extends { id: T; visible: boolean; colSpan: number; order: number },
>(
  drafts: LayoutEditorDraftItem[],
  sections: readonly { id: T }[],
  defaultColSpan: Record<T, number>,
  normalizeColSpan: (span: number | null | undefined) => number,
): Record<T, L> {
  const editorById = new Map(drafts.map((d) => [d.id, d]));
  const layout = {} as Record<T, L>;
  sections.forEach((s, idx) => {
    const sid = s.id;
    const d = editorById.get(sid);
    layout[sid] = {
      id: sid,
      visible: d?.visible !== false,
      colSpan: normalizeColSpan(d?.colSpan ?? defaultColSpan[sid] ?? 12),
      order: d?.order ?? idx,
    } as L;
  });
  const ordered = (Object.values(layout) as L[]).sort((a, b) => a.order - b.order);
  ordered.forEach((item, idx) => (item.order = idx));
  return layout;
}
